<template>
    <div class="consumption-actions">
        <h2 class="kv-title mb-4 section-title">{{ t('balance-consumption_by_action') }}</h2>

        <Grid 
            source="transactions/consumption-breakdown" 
            :filters="filters" 
            :showSearch="false" 
            :quantityPerPage="8"
            :inline-loader="true"
        >
            <template slot="rows" slot-scope="props">
                <thead>
                    <tr>
                        <th style="width: 45%; min-width: 280px;">{{ t('balance-action') }}</th>
                        <th style="width: 15%; min-width: 110px;">{{ t('balance-category') }}</th>
                        <th style="width: 20%; min-width: 150px;">{{ t('balance-trigger') }}</th>
                        <th class="text-right text-nowrap" style="width: 20%; min-width: 140px;">
                            <span>{{ t('balance-total') }}</span><span class="total-header-spacer"></span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in props.data" :key="index">
                        <td>
                            <div class="action-cell-content">
                                <div class="action-icon-box" :class="'type-' + (item.type || 'other')">
                                    <Icon :icon-class="getIcon(item)" size="4" />
                                </div>
                                <div class="action-title-wrapper">
                                    <KvTooltip :text="formatItemTitle(item)" placement="top">
                                        <span class="action-title">{{ formatItemTitle(item) }}</span>
                                    </KvTooltip>
                                </div>
                            </div>
                        </td>
                        <td>
                            <span class="cell-metadata">{{ translateType(item.type) }}</span>
                        </td>
                        <td>
                            <span v-if="item.trigger_type" class="cell-metadata">
                                {{ formatTriggerName(item.trigger_type) }}
                            </span>
                            <span v-else class="cell-metadata text-muted">—</span>
                        </td>
                        <td class="text-right text-nowrap total-cell">
                            <span class="total-value">{{ formatNumber(item.usage, 2) }}</span>
                            <span class="total-percent">({{ getPercentage(item.usage) }}%)</span>
                        </td>
                    </tr>
                    <tr v-if="!props.data.length">
                        <td colspan="4" class="text-center text-muted py-5">{{ t('balance-no_consumption_for_period') }}</td>
                    </tr>
                </tbody>
            </template>
            <template slot="list" slot-scope="props">
                <div class="consumption-card-list">
                    <div 
                        v-for="(item, index) in props.data" 
                        :key="index"
                        class="consumption-card-item"
                    >
                        <div class="d-flex align-items-center justify-content-between mb-2">
                            <div class="d-flex align-items-center min-w-0 mr-2 flex-grow-1 overflow-hidden">
                                <div class="action-icon-box" :class="'type-' + (item.type || 'other')">
                                    <Icon :icon-class="getIcon(item)" size="4" />
                                </div>
                                <div class="action-title-wrapper">
                                    <span class="action-title font-weight-500" :title="formatItemTitle(item)">
                                        {{ formatItemTitle(item) }}
                                    </span>
                                </div>
                            </div>
                            <div class="text-right flex-shrink-0">
                                <div class="total-value font-weight-600 text-dark">
                                    {{ formatNumber(item.usage, 2) }}
                                </div>
                                <small class="text-muted total-percent-sm">
                                    {{ getPercentage(item.usage) }}%
                                </small>
                            </div>
                        </div>

                        <div class="d-flex align-items-center flex-wrap mt-1 pt-2 consumption-item-footer">
                            <span class="kv-badge-item badge-type">
                                {{ translateType(item.type) }}
                            </span>
                            <span v-if="item.trigger_type" class="kv-badge-item badge-trigger">
                                {{ formatTriggerName(item.trigger_type) }}
                            </span>
                        </div>
                    </div>

                    <div v-if="!props.data.length" class="text-center text-muted py-5">
                        {{ t('balance-no_consumption_for_period') }}
                    </div>
                </div>
            </template>
        </Grid>
    </div>
</template>

<script>
import Grid from "../../components/grid/grid.vue";
import Icon from "../../components/icon/icon.vue";
import KvTooltip from "../../components/ui/kv-tooltip.vue";
import FormatMixin from "../mixins/format-mixin.js";
import { getAutomationIcon } from "../utils/automation-icons.js";

export default {
    name: "ConsumptionActions",
    mixins: [FormatMixin],
    components: {
        Grid,
        Icon,
        KvTooltip
    },
    props: {
        filters: { type: Object, default: () => ({}) },
        totalConsumed: { type: Number, default: 0 }
    },
    methods: {
        getIcon(item) {
            return getAutomationIcon(item, 'trigger_type');
        },
        translateType(type) {
            const keyMap = {
                'automation': 'balance-automation',
                'campaign': 'balance-campaign',
                'other': 'balance-other'
            };
            const key = keyMap[type] || 'balance-other';
            return this.t(key);
        },
        formatTriggerName(trigger) {
            if (!trigger) return '';
            const triggerMap = {
                'birthday': 'balance-trigger_birthday',
                'specific_date': 'balance-trigger_specific_date',
                'inactive': 'balance-trigger_inactivity',
                'inactivity': 'balance-trigger_inactivity',
                'registration': 'balance-trigger_registration',
                'purchase': 'balance-trigger_purchase',
                'point_accumulated': 'balance-trigger_points'
            };
            const translationKey = triggerMap[trigger] || null;
            if (translationKey) {
                return this.t(translationKey);
            }

            const globalKey = 'automations-triggers-' + trigger;
            const globalTranslation = this.t(globalKey);
            if (globalTranslation && globalTranslation !== globalKey && globalTranslation !== '') {
                return globalTranslation;
            }

            return trigger.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        },
        formatItemTitle(item) {
            if (item.type !== 'other') {
                return this.$_(item.title) || item.title;
            }
            return this.formatActionType(item.title);
        },
        getPercentage(usage) {
            if (!this.totalConsumed) return "0.0";
            return this.formatPercent((usage / this.totalConsumed) * 100);
        }
    }
};
</script>

<style scoped lang="scss">
.section-title {
    font-size: 20px;
}

.action-cell-content {
    display: flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
}

.action-icon-box {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
    background-color: #F8FAFC;
    border: 1px solid #E2E8F0;
    color: #4B5563;
}

.action-title-wrapper {
    min-width: 0;
    flex: 1;
    overflow: hidden;

    ::v-deep .kv-tooltip-container {
        display: block;
        max-width: 100%;
    }
}

.action-title {
    font-size: 13px;
    font-weight: 500;
    color: #111827;
    line-height: 1.4;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: default;
}

.cell-metadata {
    font-size: 13px;
    font-weight: 400;
    color: #4B5563;
    line-height: 1.4;
}

.total-cell {
    font-size: 13px;
    line-height: 1.4;

    .total-value {
        font-weight: 500;
        color: #111827;
    }

    .total-percent {
        font-weight: 400;
        color: #6B7280;
        display: inline-block;
        width: 60px;
        text-align: right;
    }
}

.total-header-spacer {
    display: inline-block;
    width: 60px;
}

::v-deep .grid {
    .table-responsive {
        @media (max-width: 768px) {
            margin: 0 -16px;
            width: calc(100% + 32px);
        }
    }

    .table td,
    .table th {
        padding: 0 20px;
        text-align: left;

        @media (max-width: 576px) {
            padding: 0 12px;
        }
    }

    .table td.text-right,
    .table th.text-right {
        text-align: right !important;
    }

    .table thead th {
        height: auto !important;
        padding-top: 18px !important;
        padding-bottom: 14px !important;
        padding-left: 20px !important;
        padding-right: 20px !important;
        vertical-align: middle !important;
        line-height: 1.2;
        font-weight: 500;
        text-transform: uppercase;
        font-size: 12px;
        letter-spacing: 0.05em;
        background-color: #2AAA4C;
        color: #FFFFFF;
        border: none;

        @media (max-width: 576px) {
            padding-left: 12px !important;
            padding-right: 12px !important;
        }

        &:first-child {
            border-top-left-radius: 8px;
            @media (max-width: 768px) {
                border-top-left-radius: 0;
            }
        }

        &:last-child {
            border-top-right-radius: 8px;
            @media (max-width: 768px) {
                border-top-right-radius: 0;
            }
        }
    }

    .table tbody tr td {
        background-color: #FFFFFF;
        height: 60px;
        vertical-align: middle;
        color: #374151;
        border-bottom: 1px solid #F3F4F6;
        border-top: none;
    }

    .table tbody tr:hover td {
        background-color: #F8FAFC;
    }

    .table-wrapper {
        border-radius: 16px;
        border: 1px solid #E5E7EB;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;

        @media (max-width: 768px) {
            margin: 0 -16px;
            width: calc(100% + 32px);
            border-radius: 0;
            border-left: none;
            border-right: none;
        }
    }

    table {
        min-width: 700px;
    }
}

.consumption-card-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0;
}

.consumption-card-item {
    background: #FFFFFF;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    padding: 14px 16px;
    transition: background 0.15s ease;

    &:hover {
        background-color: #F8FAFC;
    }
}

.consumption-item-footer {
    border-top: 1px dashed #F1F5F9;
    gap: 6px;
}

.kv-badge-item {
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 6px;
    background: #F3F4F6;
    color: #4B5563;

    &.badge-type {
        background: #F1FCF3;
        color: #15803D;
        border: 1px solid #DCFCE7;
    }

    &.badge-trigger {
        background: #F8FAFC;
        color: #475569;
        border: 1px solid #E2E8F0;
    }
}

.total-percent-sm {
    font-size: 12px;
    display: block;
}
</style>
